import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarClock, Loader2, Search, ShieldAlert, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import RecruiterBooking from "./RecruiterBooking";

// Book a Meeting: choose an AUTHORIZED Host (Booking Access enforced server-side), then the shared
// recruiter-assisted flow. The Host owns the calendar + Teams meeting; Booked By is the logged-in user.
export default function BookMeeting() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [hostId, setHostId] = useState("");
  const [q, setQ] = useState("");
  const wantHost = sp.get("host") || "";
  const wantType = sp.get("type") || "";

  useEffect(() => {
    api.get("/booking/hosts").then((r) => {
      setData(r.data);
      const hosts = r.data.hosts || [];
      const match = wantHost ? hosts.find((h) => h.host_user_id === wantHost && (!wantType || h.appointment_types.some((t) => t.id === wantType))) : null;
      if (wantHost && !match) toast.error("You don't have booking access for that host or appointment type.");
      setHostId((match || (hosts.length === 1 ? hosts[0] : null))?.page_id || "");
    }).catch((e) => { toast.error(apiError(e)); setData({ hosts: [], can_host: false }); });
  }, [wantHost, wantType]);

  const hosts = useMemo(() => data?.hosts || [], [data]);
  const host = hosts.find((h) => h.page_id === hostId) || null;
  const shown = hosts.filter((h) => !q || `${h.name} ${h.title} ${h.organization}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="book-meeting-page">
      <PageHeader title="Book a Meeting" description="Select an authorized host to schedule a meeting for a candidate or other Person." />
      {data === null ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : hosts.length === 0 ? (
        <NoAccess canHost={data.can_host} navigate={navigate} />
      ) : host ? (
        <div className="space-y-3">
          {hosts.length > 1 && (
            <button onClick={() => setHostId("")} className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1" data-testid="book-meeting-change-host">
              <ChevronLeft className="w-4 h-4" />Choose a different host
            </button>
          )}
          <HostCard h={host} selected />
          <RecruiterBooking key={host.page_id} inline initialTypeId={wantHost === host.host_user_id ? wantType : ""}
            page={{ id: host.page_id, name: host.page_name, timezone: host.timezone, can_override: host.can_override,
                    appointment_types: host.appointment_types, interest_options: host.interest_options,
                    branding: { display_name: host.name } }}
            onClose={() => navigate("/calendar?tab=booked")} onBooked={() => {}} />
        </div>
      ) : (
        <div className="max-w-3xl">
          <p className="text-sm font-semibold text-slate-700 mb-2">Who would you like to book with?</p>
          {hosts.length > 5 && (
            <div className="relative mb-3 max-w-sm">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hosts…" className="pl-8" data-testid="book-meeting-host-search" />
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3" data-testid="book-meeting-host-list">
            {shown.map((h) => <HostCard key={h.page_id} h={h} onPick={() => setHostId(h.page_id)} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function HostCard({ h, onPick, selected }) {
  const initials = (h.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className={`rounded-xl border bg-white p-4 flex flex-wrap items-center gap-3 ${selected ? "border-brand/40 max-w-2xl" : "border-slate-200 hover:border-brand/50 transition-colors"}`} data-testid={`host-card-${h.host_user_id}`}>
      {h.photo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${h.photo_url}`} alt="" className="w-12 h-12 rounded-full object-cover" />
        : <div className="w-12 h-12 rounded-full bg-brand/10 text-brand font-semibold flex items-center justify-center">{initials}</div>}
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-800 truncate">{h.name}{h.is_me ? " (me)" : ""}</p>
        {h.title && <p className="text-xs text-slate-500 truncate">{h.title}</p>}
        {h.organization && <p className="text-xs text-slate-400 truncate">{h.organization}</p>}
      </div>
      {onPick && <Button size="sm" className="bg-brand hover:bg-brand-strong shrink-0" onClick={onPick} data-testid={`book-with-${h.host_user_id}`}>Book with {h.name.split(" ")[0]}</Button>}
    </div>
  );
}

function NoAccess({ canHost, navigate }) {
  return (
    <div className="space-y-4 max-w-2xl">
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="book-meeting-empty">
        <ShieldAlert className="w-8 h-8 mx-auto text-slate-300 mb-2" />
        <p className="text-slate-600">You do not currently have permission to book with another Top-Recruit host. Contact your recruiting administrator if you believe you should have booking access.</p>
      </div>
      {canHost && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 flex items-center justify-between gap-3" data-testid="book-meeting-host-cta">
          <div><p className="font-semibold text-slate-800 text-sm">Want others to book with you?</p><p className="text-xs text-slate-500">Create your own Scheduling Page.</p></div>
          <Button variant="outline" size="sm" onClick={() => navigate("/calendar/scheduling-pages")}><CalendarClock className="w-4 h-4 mr-1.5" />Scheduling Pages</Button>
        </div>
      )}
    </div>
  );
}
